import React, { useState, useEffect, useMemo } from 'react'
import { FaUpload, FaDownload, FaPlay } from 'react-icons/fa'
import FamiliesView from './FamiliesView'
import SuppliersView from './SuppliersView'
import NetworkGraphView from './NetworkGraphView'
import OpportunitiesView from './OpportunitiesView'
import { computePricePositions, computeOpportunities, deduplicatedSavings, formatEur } from './networkUtils'

export default function SupplyChainAnalytics({ spacePath, folderName, networkData, loading, onRefresh }) {
  const [subTab, setSubTab] = useState('opportunities')
  const [demoMode, setDemoMode] = useState(false)
  const [demoData, setDemoData] = useState(null)
  const [demoLoading, setDemoLoading] = useState(false)

  // Exit demo mode when navigating to a different space/folder
  useEffect(() => { setDemoMode(false); setDemoData(null) }, [spacePath, folderName])

  const hasData = networkData?.has_supplier_data || demoMode

  const activeData = demoMode ? demoData : networkData
  const stats = activeData?.stats || {}
  const parts = activeData?.parts || []
  const families = activeData?.families || []
  const clusters = activeData?.clusters || []

  // Live KPIs computed from the same logic as views (must be before early returns)
  const liveKpis = useMemo(() => {
    if (!parts.length) return null
    const enriched = computePricePositions(parts)
    const opps = computeOpportunities(enriched)
    const totalSavings = deduplicatedSavings(opps)
    const cohortSet = new Set()
    enriched.forEach(p => {
      const s = (p.suppliers || []).find(sup => sup.cohort_name)
      if (s) cohortSet.add(s.cohort_name)
    })
    return {
      opportunities: opps.length,
      savings: totalSavings,
      families: cohortSet.size,
    }
  }, [parts])

  if (!spacePath || !folderName) {
    return (
      <div className="text-center py-20 text-gray-400">
        <p className="text-lg">Select a space and folder to view supply chain analytics</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="text-center py-20 text-gray-400">
        <div className="inline-block w-8 h-8 border-2 border-gray-300 border-t-[#1e5045] rounded-full animate-spin" />
        <p className="mt-3 text-sm">Loading supply chain data...</p>
      </div>
    )
  }

  const handleDemoMode = async () => {
    setDemoLoading(true)
    try {
      const token = sessionStorage.getItem("userToken")
      const API_BASE = process.env.REACT_APP_BE_MAIN_URL
      const res = await fetch(
        `${API_BASE}/ocr/network/atlas-browse/?space_path=${encodeURIComponent(spacePath)}&folder_name=${encodeURIComponent(folderName)}&page=1&page_size=5000`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      let files = []
      if (res.ok) {
        const data = await res.json()
        files = data.results || []
      }
      const generated = generateMockNetworkData(files)
      setDemoData(generated)
      setDemoMode(true)
    } catch {
      // Fallback with no files
      setDemoData(generateMockNetworkData([]))
      setDemoMode(true)
    } finally {
      setDemoLoading(false)
    }
  }

  if (!hasData) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
          <FaUpload className="text-gray-400" size={24} />
        </div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No Purchase History</h3>
        <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
          Upload your purchase history (XLSX, CSV, or TSV) to enable supply chain analytics.
          Multi-sheet Excel files with different formats per tab are supported.
        </p>
        <div className="flex items-center justify-center gap-3">
          <DownloadTemplateButton spacePath={spacePath} folderName={folderName} />
          <UploadButton spacePath={spacePath} folderName={folderName} onSuccess={onRefresh} />
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 max-w-md mx-auto">
          <button
            onClick={handleDemoMode}
            disabled={demoLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#1e5045] border border-[#1e5045]/30 rounded-lg hover:bg-[#1e5045]/5 transition-colors disabled:opacity-50"
          >
            <FaPlay size={10} />
            {demoLoading ? 'Generating...' : 'Try Demo Mode'}
          </button>
          <p className="text-[11px] text-gray-400 mt-2">Preview with simulated data — resets when you navigate away</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Action bar — update purchase history without leaving the data view */}
      {!demoMode && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {stats.total_parts || 0} parts matched to purchase history
          </span>
          <div className="flex items-center gap-2">
            <DownloadTemplateButton spacePath={spacePath} folderName={folderName} />
            <UploadButton
              spacePath={spacePath}
              folderName={folderName}
              onSuccess={onRefresh}
              label="Update Purchase History"
            />
          </div>
        </div>
      )}

      {/* Demo mode banner */}
      {demoMode && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
          <div className="flex items-center gap-2">
            <span className="text-amber-600 text-sm font-medium">Demo Mode</span>
            <span className="text-amber-500 text-xs">Showing simulated purchase data — this is not real</span>
          </div>
          <button
            onClick={() => { setDemoMode(false); setDemoData(null) }}
            className="text-xs font-medium text-amber-700 hover:text-amber-900 underline"
          >
            Exit Demo
          </button>
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Parts" value={stats.total_parts || 0} />
        <StatCard label="Cohorts" value={liveKpis?.families || stats.total_families || 0} />
        <StatCard label="Suppliers" value={stats.total_suppliers || 0} />
        <StatCard label="Total Spend" value={`€${((stats.total_spend || 0) / 1e6).toFixed(1)}M`} accent />
        <StatCard label="Opportunities" value={liveKpis?.opportunities || 0} sub={liveKpis ? `~${formatEur(liveKpis.savings)} savings` : ''} accent />
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-0 border-b border-gray-200">
        {['opportunities', 'network', 'families', 'suppliers'].map(tab => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors capitalize ${
              subTab === tab
                ? 'text-[#1e5045] border-[#1e5045] font-semibold'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      {subTab === 'opportunities' && (
        <OpportunitiesView parts={parts} stats={stats} />
      )}
      {subTab === 'network' && (
        <NetworkGraphView
          spacePath={spacePath}
          folderName={folderName}
          networkData={networkData}
        />
      )}
      {subTab === 'families' && (
        <FamiliesView parts={parts} families={families} clusters={clusters} />
      )}
      {subTab === 'suppliers' && (
        <SuppliersView parts={parts} />
      )}
    </div>
  )
}

function StatCard({ label, value, accent, sub }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm">
      <div className={`text-xl font-bold ${accent ? 'text-[#1e5045]' : 'text-gray-800'}`}>{value}</div>
      <div className="text-[11px] text-gray-500 uppercase tracking-wide font-medium">{label}</div>
      {sub && <div className="text-[10px] text-gray-400 mt-0.5">{sub}</div>}
    </div>
  )
}

function UploadButton({ spacePath, folderName, onSuccess, label = 'Upload Purchase History' }) {
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)
  const [clarification, setClarification] = useState(null)
  const [clarFile, setClarFile] = useState(null)

  const handleUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    e.target.value = '' // Reset so same file can be re-selected
    setUploading(true)
    setUploadResult(null)
    setClarification(null)
    const token = sessionStorage.getItem("userToken")
    const formData = new FormData()
    formData.append('file', file)
    formData.append('space_path', spacePath)
    formData.append('folder_name', folderName)

    try {
      const API_BASE = process.env.REACT_APP_BE_MAIN_URL
      const res = await fetch(`${API_BASE}/ocr/network/supplier-bom/upload/`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      const data = await res.json()

      if (!res.ok) {
        setUploadResult({ status: 'error', error: data.error || 'Upload failed' })
      } else if (data.status === 'clarification_needed') {
        setClarification(data)
        setClarFile(file)
      } else {
        setUploadResult(data)
          // Delay onSuccess() to allow modal to render first
          setTimeout(() => onSuccess(), 500)
      }
    } catch (err) {
      setUploadResult({ status: 'error', error: 'Network error. Please try again.' })
    } finally {
      setUploading(false)
    }
  }

  const handleClarificationSubmit = async (mappings) => {
    setUploading(true)
    const token = sessionStorage.getItem("userToken")

    try {
      const API_BASE = process.env.REACT_APP_BE_MAIN_URL
      const res = await fetch(`${API_BASE}/ocr/network/supplier-bom/upload/confirm/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: clarification.session_id,
          clarifications: JSON.stringify(mappings),
          space_path: spacePath,
          folder_name: folderName,
        }),
      })
      const data = await res.json()

      if (!res.ok) {
        setUploadResult({ status: 'error', error: data.error || 'Confirmation failed' })
      } else {
        setUploadResult(data)
          // Delay onSuccess() to allow modal to render first
          setTimeout(() => onSuccess(), 500)
      }
    } catch (err) {
      setUploadResult({ status: 'error', error: 'Network error. Please try again.' })
    } finally {
      setUploading(false)
      setClarification(null)
      setClarFile(null)
    }
  }

  return (
    <>
      <label className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e5045] text-white text-sm font-medium rounded-lg cursor-pointer hover:bg-[#0d5d57] transition-colors">
        <FaUpload size={14} />
        {uploading ? 'Processing...' : label}
        <input type="file" accept=".xlsx,.csv,.tsv" onChange={handleUpload} className="hidden" />
      </label>

      {clarification && (
        <ClarificationModal
          data={clarification}
          onSubmit={handleClarificationSubmit}
          onCancel={() => { setClarification(null); setClarFile(null) }}
        />
      )}

      {uploadResult && (
        <UploadResultsModal
          result={uploadResult}
          onClose={() => setUploadResult(null)}
        />
      )}
    </>
  )
}

// ─── Clarification Modal ─────────────────────────────────────────────────────

function ClarificationModal({ data, onSubmit, onCancel }) {
  const [mappings, setMappings] = useState(() => {
    const init = {}
    data.sheets_needing_clarification.forEach(sheet => {
      init[sheet.sheet_name] = { ...sheet.auto_detected }
    })
    return init
  })

  const targetFields = [
    { key: 'filename', label: 'Drawing Filename', required: true },
    { key: 'supplier', label: 'Supplier / Vendor', required: true },
    { key: 'pn', label: 'Part Number', required: false },
    { key: 'unit_cost', label: 'Unit Cost / Price', required: false },
    { key: 'spend', label: 'Total Spend', required: false },
    { key: 'eau', label: 'Annual Volume (EAU)', required: false },
    { key: 'description', label: 'Description', required: false },
  ]

  const updateMapping = (sheetName, field, value) => {
    setMappings(prev => ({
      ...prev,
      [sheetName]: { ...prev[sheetName], [field]: value || null }
    }))
  }

  const canSubmit = data.sheets_needing_clarification.every(sheet => {
    const m = mappings[sheet.sheet_name]
    // supplier_hint from triage means supplier is implied by tab name
    const hasSupplier = m?.supplier || sheet.supplier_hint
    return m?.filename && hasSupplier
  })

  const skippedSheets = data.sheets_skipped || {}
  const skippedNames = Object.keys(skippedSheets)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Column Mapping Required</h3>
          <p className="text-sm text-gray-500 mt-1">{data.message}</p>
          {data.sheets_ok?.length > 0 && (
            <p className="text-xs text-green-600 mt-1">
              ✓ {data.sheets_ok.length} sheet(s) auto-mapped successfully
            </p>
          )}
          {skippedNames.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              {skippedNames.length} sheet(s) skipped (not purchasing data): {skippedNames.join(', ')}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {data.sheets_needing_clarification.map(sheet => (
            <div key={sheet.sheet_name} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-gray-700">Sheet: {sheet.sheet_name}</span>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                  {Math.round(sheet.confidence * 100)}% confidence
                </span>
                {sheet.supplier_hint && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    Supplier: {sheet.supplier_hint}
                  </span>
                )}
              </div>
              {sheet.reasoning && (
                <p className="text-xs text-gray-500 mb-3 italic">{sheet.reasoning}</p>
              )}
              {sheet.supplier_hint && (
                <p className="text-xs text-blue-600 mb-2">
                  ℹ Tab name identified as supplier — no supplier column needed
                </p>
              )}

              {/* Sample data preview */}
              {sheet.sample_rows?.length > 0 && (
                <div className="mb-4 overflow-x-auto">
                  <p className="text-xs text-gray-500 mb-1 font-medium">Data preview:</p>
                  <table className="text-[11px] border border-gray-200 w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        {sheet.headers.slice(0, 8).map(h => (
                          <th key={h} className="px-2 py-1 text-left font-medium text-gray-600 border-b">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sheet.sample_rows.slice(0, 2).map((row, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          {sheet.headers.slice(0, 8).map(h => (
                            <td key={h} className="px-2 py-1 text-gray-600 truncate max-w-[120px]">{row[h] || ''}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Column mapping selectors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {targetFields.map(field => (
                  <div key={field.key} className="flex items-center gap-2">
                    <label className="text-xs text-gray-600 w-28 flex-shrink-0">
                      {field.label}{field.required && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      className="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
                      value={mappings[sheet.sheet_name]?.[field.key] || ''}
                      onChange={e => updateMapping(sheet.sheet_name, field.key, e.target.value)}
                    >
                      <option value="">— not mapped —</option>
                      {sheet.headers.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(mappings)}
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-[#1e5045] rounded-lg hover:bg-[#0d5d57] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm & Process
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Upload Results Modal ────────────────────────────────────────────────────

function UploadResultsModal({ result, onClose }) {
  const [expanded, setExpanded] = useState(false)

  if (result.status === 'error') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-red-600 text-lg font-bold">!</span>
            </div>
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-800">Upload Failed</h3>
              <p className="text-sm text-red-600 mt-1">{result.error}</p>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              Close
            </button>
          </div>
        </div>
      </div>
    )
  }

  const summary = result.summary || {}
  const issues = result.issues || []
  const columnMappings = result.column_mappings || {}
  const hasIssues = issues.length > 0
  const isFullSuccess = summary.skipped === 0 && summary.failed === 0

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col">
        <div className={`px-6 py-4 border-b ${isFullSuccess ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isFullSuccess ? 'bg-green-200' : 'bg-amber-200'}`}>
              <span className={`text-lg font-bold ${isFullSuccess ? 'text-green-700' : 'text-amber-700'}`}>
                {isFullSuccess ? '✓' : '!'}
              </span>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-800">
                {summary.imported} of {summary.total_rows} rows imported
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {summary.unique_files} files · {summary.unique_suppliers} suppliers · {summary.sheets_processed} sheet(s)
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-green-50 rounded-lg px-3 py-2">
              <div className="text-lg font-bold text-green-700">{summary.imported}</div>
              <div className="text-[10px] text-green-600 uppercase">Imported</div>
            </div>
            {summary.skipped > 0 && (
              <div className="bg-amber-50 rounded-lg px-3 py-2">
                <div className="text-lg font-bold text-amber-700">{summary.skipped}</div>
                <div className="text-[10px] text-amber-600 uppercase">Skipped</div>
              </div>
            )}
            {summary.failed > 0 && (
              <div className="bg-red-50 rounded-lg px-3 py-2">
                <div className="text-lg font-bold text-red-700">{summary.failed}</div>
                <div className="text-[10px] text-red-600 uppercase">Failed</div>
              </div>
            )}
            {summary.resolved_by_ai > 0 && (
              <div className="bg-blue-50 rounded-lg px-3 py-2">
                <div className="text-lg font-bold text-blue-700">{summary.resolved_by_ai}</div>
                <div className="text-[10px] text-blue-600 uppercase">AI Resolved</div>
              </div>
            )}
          </div>

          {/* AI-assisted info */}
          {summary.llm_assisted_sheets > 0 && (
            <div className="flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
              <span className="text-blue-600 font-medium">SourceOptima AI</span>
              <span className="text-blue-500">
                identified columns for {summary.llm_assisted_sheets} sheet(s)
              </span>
            </div>
          )}

          {/* Column mappings */}
          {Object.keys(columnMappings).length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-600 mb-1">Column mappings used:</p>
              <div className="space-y-1">
                {Object.entries(columnMappings).map(([sheet, info]) => (
                  <div key={sheet} className="text-xs text-gray-500 bg-gray-50 rounded px-2 py-1.5">
                    <span className="font-medium text-gray-700">{sheet}</span>
                    <span className="mx-1">·</span>
                    <span className="capitalize">{info.method === 'gemini' ? 'AI-detected' : info.method}</span>
                    <span className="mx-1">·</span>
                    {Object.entries(info.mapping).map(([target, source]) => (
                      <span key={target} className="inline-block mr-2">
                        {source} → <span className="font-medium">{target}</span>
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Issues table */}
          {hasIssues && (
            <div>
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs font-medium text-gray-600 hover:text-gray-800 flex items-center gap-1"
              >
                <span>{expanded ? '▼' : '▶'}</span>
                {issues.length} issue(s) {result.issues_truncated && '(showing first 100)'}
              </button>
              {expanded && (
                <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-[11px]">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600">Row</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600">Sheet</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600">Status</th>
                        <th className="px-2 py-1.5 text-left font-medium text-gray-600">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {issues.map((issue, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-2 py-1 text-gray-600">{issue.row}</td>
                          <td className="px-2 py-1 text-gray-600">{issue.sheet}</td>
                          <td className="px-2 py-1">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              issue.severity === 'skipped' ? 'bg-amber-100 text-amber-700' :
                              issue.severity === 'error' ? 'bg-red-100 text-red-700' :
                              issue.severity === 'resolved' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {issue.severity}
                            </span>
                          </td>
                          <td className="px-2 py-1 text-gray-600">{issue.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-[#1e5045] rounded-lg hover:bg-[#0d5d57]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

function DownloadTemplateButton({ spacePath, folderName }) {
  const [loading, setLoading] = useState(false)

  const handleDownload = async () => {
    const headers = [
      'drawing_filename',
      'part_number',
      'description',
      'supplier',
      'unit_cost',
      'annual_spend',
      'eau',
    ]

    let rows = []
    try {
      setLoading(true)
      const token = sessionStorage.getItem("userToken")
      const API_BASE = process.env.REACT_APP_BE_MAIN_URL
      const res = await fetch(
        `${API_BASE}/ocr/network/atlas-browse/?space_path=${encodeURIComponent(spacePath)}&folder_name=${encodeURIComponent(folderName)}&page=1&page_size=5000`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      if (res.ok) {
        const data = await res.json()
        const results = data.results || []
        rows = results.map(r => [
          r.filename || '',
          r.part_number || '',
          r.part_name || '',
          '', // supplier
          '', // unit_cost
          '', // annual_spend
          '', // eau
        ])
      }
    } catch {
      // Fall back to empty template
    } finally {
      setLoading(false)
    }

    // If no files found, provide example rows
    if (rows.length === 0) {
      rows = [
        ['example-drawing.pdf', 'PN-001', 'Part Description', 'Supplier Name', '12.50', '15000', '1200'],
      ]
    }

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'purchase_history_template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
    >
      <FaDownload size={14} />
      {loading ? 'Generating...' : 'Download Template'}
    </button>
  )
}

// ─── Demo Mode: Mock Data Generator ──────────────────────────────────────────

const DEMO_SUPPLIERS = [
  'Precision Machining Inc.', 'Advanced Alloys Corp.', 'Pacific Manufacturing Ltd.',
  'EuroTech Precision GmbH', 'Atlas Metal Works', 'Sterling Components',
  'ProCut Industries', 'TitanForm LLC',
]

function generateMockNetworkData(atlasFiles) {
  const rand = (min, max) => Math.random() * (max - min) + min
  const pick = arr => arr[Math.floor(Math.random() * arr.length)]

  // Use real files if available, otherwise generate placeholders
  const files = atlasFiles.length > 0
    ? atlasFiles
    : Array.from({ length: 20 }, (_, i) => ({
        filename: `DEMO-${1000 + i}-A-110-01.pdf`,
        part_number: `PN-${1000 + i}`,
        part_name: `Demo Part ${i + 1}`,
        material_family: pick(['Aluminum', 'Stainless Steel', 'Titanium', 'Steel', 'Copper']),
        primary_process: pick(['machining', 'sheet_metal', 'weldment', 'assembly']),
        envelope_class: pick(['S', 'M', 'L']),
      }))

  const parts = []
  const familyMap = {}
  let totalSpend = 0
  let multiSourceCount = 0

  files.forEach(f => {
    const method = f.primary_process || pick(['machining', 'sheet_metal', 'weldment', 'assembly'])
    const material = f.material_family || pick(['Aluminum', 'Stainless Steel', 'Titanium'])
    const size = f.envelope_class || pick(['S', 'M', 'L'])
    const family = `${method}_${(material || 'unknown').toLowerCase().replace(/\s+/g, '_')}_${size.toLowerCase()}`

    // 1-3 suppliers per part
    const numSuppliers = Math.random() < 0.3 ? Math.floor(rand(2, 4)) : 1
    if (numSuppliers > 1) multiSourceCount++
    const suppliers = []
    const usedSuppliers = new Set()
    for (let i = 0; i < numSuppliers; i++) {
      let sup = pick(DEMO_SUPPLIERS)
      while (usedSuppliers.has(sup)) sup = pick(DEMO_SUPPLIERS)
      usedSuppliers.add(sup)
      const unitCost = rand(5, 500)
      const eau = Math.floor(rand(50, 5000))
      const spend = unitCost * eau
      suppliers.push({ name: sup, spend, unit_cost: unitCost, eau: String(eau) })
    }

    const partSpend = suppliers.reduce((s, sup) => s + sup.spend, 0)
    totalSpend += partSpend

    if (!familyMap[family]) {
      familyMap[family] = { parts: 0, suppliers: new Set(), spend: 0, method, material, size }
    }
    familyMap[family].parts++
    familyMap[family].spend += partSpend
    suppliers.forEach(s => familyMap[family].suppliers.add(s.name))

    parts.push({
      file_id: f.file_id || null,
      filename: f.filename,
      pn: f.part_number || f.filename.replace('.pdf', ''),
      family,
      method,
      material,
      size,
      complexity: null,
      similar_count: 0,
      suppliers,
      total_spend: partSpend,
      process_family: method,
      axis_count: null,
      tightest_mm: Math.random() < 0.4 ? rand(0.01, 2.0) : null,
      tolerance_band: pick(['precision', 'standard', 'commercial']),
      axis_tier: 'axis_unspec',
      gauge_mm: null,
      gauge_band: 'gauge_unspec',
      bend_count: null,
      bend_class: 'bends_unspec',
      tightest_gdt_mm: null,
      complexity_tier: pick(['simple', 'moderate', 'complex']),
      description: f.part_name || '',
    })
  })

  const families = Object.entries(familyMap).map(([name, d]) => ({
    name,
    label: name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    parts: d.parts,
    suppliers: d.suppliers.size,
    spend: d.spend,
    method: d.method,
    material: d.material,
    size: d.size,
  }))

  // Build clusters (method x material)
  const clusterMap = {}
  families.forEach(f => {
    const key = `${f.method} · ${f.material}`
    if (!clusterMap[key]) clusterMap[key] = { families: [], parts: 0, suppliers: new Set(), total_spend: 0 }
    clusterMap[key].families.push(f.name)
    clusterMap[key].parts += f.parts
    clusterMap[key].total_spend += f.spend
  })
  const clusters = Object.entries(clusterMap).map(([name, d]) => ({
    name,
    families: d.families,
    parts: d.parts,
    suppliers: d.suppliers.size,
    total_spend: d.total_spend,
  }))

  return {
    has_supplier_data: true,
    stats: {
      total_parts: parts.length,
      total_families: families.length,
      total_suppliers: DEMO_SUPPLIERS.length,
      total_spend: totalSpend,
      multi_supplier_parts: multiSourceCount,
    },
    parts,
    families,
    clusters,
    nodes: [],
    edges: [],
  }
}
